import { useState, useEffect } from 'react';
import { chapterApi } from '../../services/api';
import { IoBookOutline, IoSearchOutline, IoChevronDownOutline } from 'react-icons/io5';
import LoadingSpinner from '../LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { getLastBook, setLastBook } from '../../utils/localPreferences';
import './ChapterCalendarSelector.css';

const ChapterCalendarSelector = ({ selectedChapters, onChaptersChange }) => {
  const { user } = useAuth();
  const [books, setBooks] = useState([]);
  const [currentBookIndex, setCurrentBookIndex] = useState(0);
  const [currentBookChapters, setCurrentBookChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [showBookSelector, setShowBookSelector] = useState(false);
  const [bookSearchTerm, setBookSearchTerm] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileRangeSelector, setShowMobileRangeSelector] = useState(false);
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');

  useEffect(() => {
    loadBooks();
    
    // Detect mobile device
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    if (books.length > 0) {
      loadCurrentBookChapters();
    }
  }, [books, currentBookIndex]);

  // Pick up where they left off rather than resetting to Genesis every visit.
  useEffect(() => {
    if (books.length === 0) return;
    const rememberedBook = getLastBook(user?.id);
    if (!rememberedBook) return;
    const rememberedIndex = books.findIndex(book => book.name === rememberedBook);
    if (rememberedIndex >= 0) {
      setCurrentBookIndex(rememberedIndex);
    }
  }, [books, user?.id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showBookSelector && !event.target.closest('.book-selector-wrapper')) {
        setShowBookSelector(false);
        setBookSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showBookSelector]);

  const loadBooks = async () => {
    try {
      setLoading(true);
      // Get all chapters to build book list
      const response = await chapterApi.getAllChapters();
      const chapters = response.data.chapters;
      
      // Group chapters by book, maintaining biblical order
      const bookMap = new Map();
      chapters.forEach(chapter => {
        if (!bookMap.has(chapter.book)) {
          bookMap.set(chapter.book, {
            name: chapter.book,
            testament: chapter.testament,
            chapters: []
          });
        }
        bookMap.get(chapter.book).chapters.push(chapter);
      });

      // Convert to array and sort chapters within each book
      const booksList = Array.from(bookMap.values()).map(book => ({
        ...book,
        chapters: book.chapters.sort((a, b) => a.chapter - b.chapter)
      }));

      // Sort books by the first chapter ID (maintains biblical order)
      booksList.sort((a, b) => a.chapters[0].id - b.chapters[0].id);

      setBooks(booksList);
      setError(null);
    } catch (err) {
      console.error('Failed to load books:', err);
      setError('Failed to load Bible books. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentBookChapters = () => {
    if (books[currentBookIndex]) {
      setCurrentBookChapters(books[currentBookIndex].chapters);
    }
  };


  const handleBookSelect = (bookIndex) => {
    setCurrentBookIndex(bookIndex);
    setLastBook(user?.id, books[bookIndex]?.name);
    setShowBookSelector(false);
    setBookSearchTerm('');
    // Clear selection when changing books
    setSelectionStart(null);
    setSelectionEnd(null);
    setIsSelecting(false);
  };

  const filteredBooks = books.filter(book => 
    book.name.toLowerCase().includes(bookSearchTerm.toLowerCase())
  );

  const toggleBookSelector = () => {
    setShowBookSelector(!showBookSelector);
    setBookSearchTerm('');
  };

  const handleChapterMouseDown = (e, chapter) => {
    if (isMobile) return; // Disable drag selection on mobile
    e.preventDefault(); // Prevent text selection
    setIsSelecting(true);
    setSelectionStart(chapter);
    setSelectionEnd(chapter);
    updateSelection(chapter, chapter);
  };

  const handleChapterMouseEnter = (chapter) => {
    if (isMobile || !isSelecting || !selectionStart) return;
    setSelectionEnd(chapter);
    updateSelection(selectionStart, chapter);
  };

  const handleChapterMouseUp = () => {
    if (isMobile) return;
    setIsSelecting(false);
  };

  const handleMobileRangeSelect = () => {
    if (!rangeStart || !rangeEnd) return;
    
    const start = parseInt(rangeStart);
    const end = parseInt(rangeEnd);
    
    if (start > end || start < 1 || end > currentBookChapters.length) {
      alert(`Please enter valid chapter numbers between 1 and ${currentBookChapters.length}`);
      return;
    }
    
    const selectedInRange = currentBookChapters
      .filter(ch => ch.chapter >= start && ch.chapter <= end)
      .map(ch => ch.id);
    
    // Add to existing selection
    const newSelection = [...new Set([...selectedChapters, ...selectedInRange])];
    onChaptersChange(newSelection);
    
    // Reset and close
    setRangeStart('');
    setRangeEnd('');
    setShowMobileRangeSelector(false);
  };

  const updateSelection = (start, end) => {
    if (!start || !end) return;

    const startChapter = Math.min(start.chapter, end.chapter);
    const endChapter = Math.max(start.chapter, end.chapter);
    
    const selectedInRange = currentBookChapters
      .filter(ch => ch.chapter >= startChapter && ch.chapter <= endChapter)
      .map(ch => ch.id);

    // Add to existing selection (don't replace)
    const newSelection = [...new Set([...selectedChapters, ...selectedInRange])];
    onChaptersChange(newSelection);
  };

  const handleChapterClick = (e, chapter) => {
    e.preventDefault();
    e.stopPropagation();
    
    const isSelected = selectedChapters.includes(chapter.id);
    
    if (isSelected) {
      // Remove from selection
      onChaptersChange(selectedChapters.filter(id => id !== chapter.id));
    } else {
      // Add to selection
      onChaptersChange([...selectedChapters, chapter.id]);
    }
  };

  const clearSelection = () => {
    onChaptersChange([]);
    setSelectionStart(null);
    setSelectionEnd(null);
  };

  const selectAllInBook = () => {
    const allChapterIds = currentBookChapters.map(ch => ch.id);
    const newSelection = [...new Set([...selectedChapters, ...allChapterIds])];
    onChaptersChange(newSelection);
  };

  const getChapterClassName = (chapter) => {
    const isSelected = selectedChapters.includes(chapter.id);
    const isInCurrentRange = selectionStart && selectionEnd && 
      chapter.chapter >= Math.min(selectionStart.chapter, selectionEnd.chapter) &&
      chapter.chapter <= Math.max(selectionStart.chapter, selectionEnd.chapter);
    
    return `chapter-cell ${isSelected ? 'selected' : ''} ${isInCurrentRange && isSelecting ? 'in-range' : ''}`;
  };

  const getSelectedChaptersInCurrentBook = () => {
    return currentBookChapters.filter(ch => selectedChapters.includes(ch.id)).length;
  };

  const getTotalSelectedChapters = () => {
    return selectedChapters.length;
  };

  if (loading) {
    return (
      <div className="chapter-calendar-loading">
        <LoadingSpinner message="Loading Bible books..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="chapter-calendar-error">
        <p>{error}</p>
        <button onClick={loadBooks} className="retry-button">Try again</button>
      </div>
    );
  }

  const currentBook = books[currentBookIndex];
  if (!currentBook) return null;

  return (
    <div className="chapter-calendar-selector">
      <div className="calendar-header">
        <div className="book-navigation">
          <div className="current-book">
            <IoBookOutline className="book-icon" />
            <div className="book-info">
              <div className="book-selector-wrapper">
                <button
                  type="button"
                  className="book-selector-button"
                  onClick={toggleBookSelector}
                >
                  <h3>{currentBook.name}</h3>
                  <IoChevronDownOutline className={`dropdown-icon ${showBookSelector ? 'open' : ''}`} />
                </button>
                {showBookSelector && (
                  <div className="book-dropdown">
                    <div className="book-search">
                      <IoSearchOutline className="search-icon" />
                      <input
                        type="text"
                        placeholder="Search books..."
                        value={bookSearchTerm}
                        onChange={(e) => setBookSearchTerm(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <div className="book-list">
                      {filteredBooks.map((book) => {
                        const originalIndex = books.findIndex(b => b.name === book.name);
                        return (
                          <button
                            key={book.name}
                            type="button"
                            className={`book-option ${originalIndex === currentBookIndex ? 'active' : ''}`}
                            onClick={() => handleBookSelect(originalIndex)}
                          >
                            <div className="book-option-content">
                              <span className="book-name">{book.name}</span>
                              <span className="book-details">
                                {book.testament} • {book.chapters.length} chapters
                              </span>
                            </div>
                          </button>
                        );
                      })}
                      {filteredBooks.length === 0 && (
                        <div className="no-books-found">
                          No books found matching "{bookSearchTerm}"
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <small>{currentBook.testament} Testament • {currentBook.chapters.length} chapters</small>
            </div>
          </div>
        </div>

        <div className="calendar-controls">
          <div className="selection-info">
            <span className="selected-count">
              {getSelectedChaptersInCurrentBook()} of {currentBook.chapters.length} chapters selected in {currentBook.name}
            </span>
            <span className="total-count">
              ({getTotalSelectedChapters()} total chapters selected)
            </span>
          </div>
          <div className="control-buttons">
            <button
              type="button"
              onClick={selectAllInBook}
              className="select-all-button"
            >
              Select all in {currentBook.name}
            </button>
            {isMobile && (
              <button
                type="button"
                onClick={() => setShowMobileRangeSelector(true)}
                className="range-select-button"
              >
                Select range
              </button>
            )}
            <button
              type="button"
              onClick={clearSelection}
              className="clear-button"
              disabled={selectedChapters.length === 0}
            >
              Clear all
            </button>
          </div>
        </div>
      </div>

      <div className="calendar-instructions">
        <p>
          {isMobile ? (
            <>
              <strong>Tap</strong> individual chapters to select them.
              <strong>Tap the book name</strong> to quickly jump to any book, or use <strong>Select range</strong> for multiple chapters.
            </>
          ) : (
            <>
              <strong>Click</strong> individual chapters or <strong>click and drag</strong> to select ranges.
              <strong>Click the book name</strong> to quickly jump to any book, or use the arrows to navigate.
            </>
          )}
        </p>
      </div>

      <div 
        className="chapter-calendar"
        onMouseUp={handleChapterMouseUp}
        onMouseLeave={handleChapterMouseUp}
      >
        {currentBookChapters.map(chapter => (
          <div
            key={chapter.id}
            className={getChapterClassName(chapter)}
            onMouseDown={isMobile ? undefined : (e) => handleChapterMouseDown(e, chapter)}
            onMouseEnter={isMobile ? undefined : () => handleChapterMouseEnter(chapter)}
            onClick={(e) => handleChapterClick(e, chapter)}
            title={`${currentBook.name} ${chapter.chapter}`}
            style={{ touchAction: 'manipulation' }} // Improve mobile touch response
          >
            <span className="chapter-number">{chapter.chapter}</span>
          </div>
        ))}
      </div>

      {selectedChapters.length > 0 && (
        <div className="selection-summary">
          <h4>Selected chapters ({selectedChapters.length})</h4>
          <div className="selected-chapters-preview">
            {selectedChapters.slice(0, 20).map(chapterId => {
              // Find the chapter info
              const chapter = books.flatMap(book => book.chapters).find(ch => ch.id === chapterId);
              return chapter ? (
                <span key={chapterId} className="chapter-tag">
                  {chapter.book} {chapter.chapter}
                </span>
              ) : null;
            })}
            {selectedChapters.length > 20 && (
              <span className="more-chapters">
                +{selectedChapters.length - 20} more...
              </span>
            )}
          </div>
        </div>
      )}

      {/* Mobile Range Selector Modal */}
      {showMobileRangeSelector && (
        <div className="mobile-range-modal">
          <div className="range-modal-content">
            <h3>Select chapter range in {currentBook.name}</h3>
            <p>Choose a range of chapters (1 to {currentBook.chapters.length})</p>
            
            <div className="range-inputs">
              <div className="range-input-group">
                <label>From chapter</label>
                <input
                  type="number"
                  min="1"
                  max={currentBook.chapters.length}
                  value={rangeStart}
                  onChange={(e) => setRangeStart(e.target.value)}
                  placeholder="1"
                />
              </div>
              <div className="range-input-group">
                <label>To chapter</label>
                <input
                  type="number"
                  min="1"
                  max={currentBook.chapters.length}
                  value={rangeEnd}
                  onChange={(e) => setRangeEnd(e.target.value)}
                  placeholder={currentBook.chapters.length.toString()}
                />
              </div>
            </div>
            
            <div className="range-modal-actions">
              <button
                type="button"
                onClick={() => setShowMobileRangeSelector(false)}
                className="cancel-button"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleMobileRangeSelect}
                className="select-range-confirm-button"
                disabled={!rangeStart || !rangeEnd}
              >
                Select range
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChapterCalendarSelector;
